/**
 * iOS CJK PUA Text Fix for pdfjs
 *
 * Problem: pdfjs assigns PUA codepoints (U+E000–F8FF) to Type0/CIDFont glyphs.
 * iOS Safari silently drops fillText() calls using PUA chars from custom CSS
 * font-faces, leaving all CJK text blank.
 *
 * Fix: before render, obtain real Unicode via getTextContent(); intercept every
 * PUA fillText call; map to real Unicode by canvas-coordinate matching; defer
 * to a fresh canvas drawn with system font only. A fresh context has no custom
 * fonts registered, so sans-serif falls back to Hiragino Sans on iOS.
 *
 * Non-iOS: zero overhead, renders normally and returns the original canvas.
 * Requires createImageBitmap (Safari 15+ / iOS 15+); degrades gracefully.
 */

import { isIos } from '@umu/umu-ua';

interface PdfjsTextItem {
    str: string;
    transform: number[];
    width?: number;
}

interface PdfjsTextContent {
    items: PdfjsTextItem[];
}

interface PdfjsViewport {
    width: number;
    height: number;
    transform: number[];
}

interface PdfjsPage {
    getTextContent(opts?: { disableCombineTextItems?: boolean }): Promise<PdfjsTextContent>;
    render(opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfjsViewport }): {
        promise: Promise<void>;
    };
}

export interface CJKFixOptions {
    isIOS?: boolean;
}

/**
 * Renders a pdfjs page to a canvas, applying iOS CJK PUA text fix when needed.
 *
 * IMPORTANT: The returned canvas may differ from the input canvas on iOS.
 * When the returned canvas differs, draw it back to the original canvas
 * (via drawImage) to avoid replacing React-managed DOM nodes.
 *
 * @param page     pdfjs PDFPageProxy
 * @param canvas   pre-sized HTMLCanvasElement
 * @param ctx      CanvasRenderingContext2D from canvas.getContext('2d')
 * @param viewport pdfjs PageViewport
 * @param options  optional configuration
 * @returns        canvas element with correct rendering
 */
export async function renderWithCJKFix(
    page: unknown,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    viewport: unknown,
    options: CJKFixOptions = {},
): Promise<HTMLCanvasElement> {
    const { isIOS = isIos } = options;
    const p = page as PdfjsPage;
    const vp = viewport as PdfjsViewport;

    if (!isIOS) {
        await p.render({ canvasContext: ctx, viewport: vp }).promise;
        return canvas;
    }

    return _renderIOS(p, canvas, ctx, vp);
}

interface _TextBlock {
    chars: string[];
    canvasX: number;
    canvasY: number;
    charWidth: number;
    idx: number;
}

interface _DeferredDraw {
    text: string;
    x: number;
    y: number;
    maxWidth: number | undefined;
    font: string;
    fillStyle: string;
    transform: { a: number; b: number; c: number; d: number; e: number; f: number };
}

function _isPUA(cp: number): boolean {
    return cp >= 0xe000 && cp <= 0xf8ff;
}

// Y 方向权重 50 倍，确保跨行字块不被错误匹配
function _findBestBlock(cx: number, cy: number, textBlocks: _TextBlock[]): _TextBlock | null {
    let best: _TextBlock | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const b of textBlocks) {
        if (b.idx >= b.chars.length) continue;
        const dx = Math.abs(cx - (b.canvasX + b.idx * b.charWidth));
        const dy = Math.abs(cy - b.canvasY);
        const score = dy * 50 + dx;
        if (score < bestScore) { bestScore = score; best = b; }
    }
    return best;
}

function _mapChars(chars: string[], best: _TextBlock | null): string {
    let mapped = '';
    for (const ch of chars) {
        const cp = ch.codePointAt(0) ?? 0;
        if (_isPUA(cp)) {
            if (best && best.idx < best.chars.length) mapped += best.chars[best.idx++];
        } else {
            mapped += ch;
            if (best) best.idx++;
        }
    }
    return mapped;
}

async function _renderIOS(
    page: PdfjsPage,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    viewport: PdfjsViewport,
): Promise<HTMLCanvasElement> {
    // Step 1: build Unicode text blocks from getTextContent
    const content = await page.getTextContent({ disableCombineTextItems: true });
    const [vta, vtb, vtc, vtd, vte, vtf] = viewport.transform;

    const textBlocks: _TextBlock[] = [];

    for (const item of content.items) {
        if (!item.str) { continue; }
        if (!Array.isArray(item.transform)) continue;
        const pdfX = item.transform[4] ?? 0;
        const pdfY = item.transform[5] ?? 0;
        const chars = Array.from(item.str);
        textBlocks.push({
            chars,
            canvasX: vta * pdfX + vtc * pdfY + vte,
            canvasY: vtb * pdfX + vtd * pdfY + vtf,
            charWidth: chars.length > 0
                ? (Math.abs(vta) * (item.width ?? 0)) / chars.length
                : 10,
            idx: 0,
        });
    }

    // Step 2: install fillText hook to intercept PUA calls
    const deferred: _DeferredDraw[] = [];
    const origFT = ctx.fillText.bind(ctx);

    const hook = function (text: string, x: number, y: number, maxWidth?: number) {
        const chars = Array.from(text);
        if (!chars.some((c) => _isPUA(c.codePointAt(0) ?? 0))) {
            return maxWidth !== undefined ? origFT(text, x, y, maxWidth) : origFT(text, x, y);
        }

        const tm = ctx.getTransform();
        const cx = tm.a * x + tm.c * y + tm.e;
        const cy = tm.b * x + tm.d * y + tm.f;
        const best = _findBestBlock(cx, cy, textBlocks);
        const mapped = _mapChars(chars, best);

        if (!mapped.trim()) return;

        const sizePx = ctx.font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '16';
        deferred.push({
            text: mapped, x, y, maxWidth,
            font: `${sizePx}px sans-serif`,
            fillStyle: typeof ctx.fillStyle === 'string' ? ctx.fillStyle : '#000000',
            transform: { a: tm.a, b: tm.b, c: tm.c, d: tm.d, e: tm.e, f: tm.f },
        });
    };

    try {
        Object.defineProperty(ctx, 'fillText', { value: hook, writable: true, configurable: true });
    } catch {
        (ctx as unknown as Record<string, unknown>).fillText = hook;
    }

    // Step 3: normal pdfjs render (hook intercepts PUA calls)
    await page.render({ canvasContext: ctx, viewport }).promise;

    if (deferred.length === 0) {
        return canvas;
    }

    // Step 4: fresh canvas — draw Unicode text, composite original behind
    try {
        const bitmap = await createImageBitmap(canvas);
        const fresh = document.createElement('canvas');
        fresh.width = canvas.width;
        fresh.height = canvas.height;
        fresh.style.cssText = canvas.style.cssText;
        const fc = fresh.getContext('2d');

        if (!fc) {
            bitmap.close();
            return canvas;
        }

        for (const d of deferred) {
            fc.save();
            fc.setTransform(d.transform.a, d.transform.b, d.transform.c, d.transform.d, d.transform.e, d.transform.f);
            fc.font = d.font;
            fc.fillStyle = d.fillStyle;
            fc.globalAlpha = 1;
            if (d.maxWidth !== undefined) fc.fillText(d.text, d.x, d.y, d.maxWidth);
            else fc.fillText(d.text, d.x, d.y);
            fc.restore();
        }

        fc.save();
        fc.setTransform(1, 0, 0, 1, 0, 0);
        fc.globalCompositeOperation = 'destination-over';
        fc.drawImage(bitmap, 0, 0);
        fc.restore();
        bitmap.close();

        return fresh;
    } catch {
        return canvas;
    }
}
